import React, { useEffect, useRef } from 'react';

import { Box } from '@opendoor/atoms/box';
import { cn } from '@opendoor/atoms/utils';
import { resolveGestureAxis } from 'components/marketplace/shared/gestureAxis';

// Swiping ~1/7 of the viewport width advances a slide (matches bricks-next).
const SWIPE_THRESHOLD_FRACTION = 7;
// Pointer travel beyond this many px counts as a drag (so the trailing click is suppressed).
const DRAG_CLICK_THRESHOLD = 10;
// Shared with mobile mini PDP panel motion (panelTransitions.ts).
const SLIDE_TRANSITION = 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)';

const nextIndex = (current: number, delta: number, count: number, wrap: boolean) => {
  const target = current + delta;
  if (wrap) return (target + count) % count;
  return Math.max(0, Math.min(count - 1, target));
};

export interface CarouselProps {
  count: number;
  index: number;
  onIndexChange: (index: number) => void;
  /** Renders the content of slide `i`. Only slides within `windowSize` of the
   * current index are rendered; the rest are empty placeholders. */
  renderSlide: (index: number) => React.ReactNode;
  ariaLabel?: string;
  /** Classes for the clipping viewport (aspect ratio, rounding, height, …). */
  className?: string;
  /** Classes for each slide cell. */
  slideClassName?: string;
  /** Whether arrow-key / swipe navigation wraps past the ends. */
  wrap?: boolean;
  /** Skip the built-in arrow-key handler (e.g. when the parent owns keyboard nav). */
  disableKeyboard?: boolean;
  windowSize?: number;
  /** Absolutely-positioned overlay (buttons, dots, counter). */
  children?: React.ReactNode;
}

/**
 * Horizontal swipe carousel built on CSS transforms + pointer events — no
 * react-native `Animated`. The slide strip is translated by whole-viewport
 * widths (`translateX(-index * 100%)`); a live finger-drag is applied
 * imperatively (transition off) and snaps to the nearest slide on release.
 */
export const Carousel: React.FC<CarouselProps> = ({
  count,
  index,
  onIndexChange,
  renderSlide,
  ariaLabel,
  className,
  slideClassName,
  wrap = false,
  disableKeyboard = false,
  windowSize = 2,
  children,
}) => {
  const viewportRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLElement>(null);
  const draggedRef = useRef(false);
  const skipNextTransitionRef = useRef(true);

  // Snap to the active slide whenever the index changes. Skip transition on the
  // first snap so carousels that mount at a non-zero index (e.g. lightbox) do
  // not animate in from slide 0.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    if (skipNextTransitionRef.current) {
      skipNextTransitionRef.current = false;
      track.style.transition = 'none';
      track.style.transform = `translateX(${-index * 100}%)`;
      track.getBoundingClientRect();
      track.style.transition = SLIDE_TRANSITION;
      return;
    }
    track.style.transition = SLIDE_TRANSITION;
    track.style.transform = `translateX(${-index * 100}%)`;
  }, [index]);

  const handlePointerDown = (event: React.PointerEvent) => {
    const track = trackRef.current;
    if (!track || count <= 1) return;
    const startX = event.clientX;
    const startY = event.clientY;
    const startIndex = index;
    draggedRef.current = false;
    let lockedHorizontal = false;
    track.style.transition = 'none';

    const handleMove = (moveEvent: PointerEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      if (!lockedHorizontal) {
        const axis = resolveGestureAxis(dx, dy);
        if (axis === 'horizontal') {
          lockedHorizontal = true;
        } else if (axis === 'vertical') {
          if (Math.abs(dx) > DRAG_CLICK_THRESHOLD || Math.abs(dy) > DRAG_CLICK_THRESHOLD) {
            draggedRef.current = true;
          }
          return;
        } else {
          return;
        }
      }
      if (Math.abs(dx) > DRAG_CLICK_THRESHOLD || Math.abs(dy) > DRAG_CLICK_THRESHOLD) {
        draggedRef.current = true;
      }
      track.style.transform = `translateX(calc(${-startIndex * 100}% + ${dx}px))`;
    };

    const handleUp = (upEvent: PointerEvent) => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);

      if (!lockedHorizontal) {
        track.style.transition = SLIDE_TRANSITION;
        track.style.transform = `translateX(${-startIndex * 100}%)`;
        return;
      }

      const dx = upEvent.clientX - startX;
      const width = viewportRef.current?.clientWidth ?? 1;
      const threshold = width / SWIPE_THRESHOLD_FRACTION;
      let target = startIndex;
      if (dx > threshold) target = nextIndex(startIndex, -1, count, wrap);
      else if (dx < -threshold) target = nextIndex(startIndex, 1, count, wrap);

      track.style.transition = SLIDE_TRANSITION;
      track.style.transform = `translateX(${-target * 100}%)`;
      if (target !== startIndex) onIndexChange(target);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disableKeyboard) return;
    if (event.key === 'ArrowLeft') {
      onIndexChange(nextIndex(index, -1, count, wrap));
    } else if (event.key === 'ArrowRight') {
      onIndexChange(nextIndex(index, 1, count, wrap));
    }
  };

  // Swallow the click that follows a swipe so overlay tap targets don't fire.
  const handleClickCapture = (event: React.MouseEvent) => {
    if (draggedRef.current) {
      event.stopPropagation();
      event.preventDefault();
      draggedRef.current = false;
    }
  };

  return (
    <Box
      ref={viewportRef}
      as="section"
      aria-label={ariaLabel || 'Photo carousel'}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      onClickCapture={handleClickCapture}
      className={cn('relative touch-pan-y overflow-hidden outline-none', className)}
    >
      <Box ref={trackRef} className="h-full w-full flex-row">
        {Array.from({ length: count }, (_, i) => {
          const withinWindow = i >= index - windowSize && i <= index + windowSize;
          return (
            <Box
              key={i}
              aria-hidden={i !== index}
              className={cn('h-full w-full shrink-0', slideClassName)}
            >
              {withinWindow ? renderSlide(i) : null}
            </Box>
          );
        })}
      </Box>
      {children}
    </Box>
  );
};
